import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as uuid from "uuid"
import { compact } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { equals, flatten, head, isNil, map, range, reject, sum } from "ramda"
import type {
    CategoriesListItem,
    CategoriesListResponse,
    SchematizedData,
    SchematizedPage,
    SchematizedPageGroup,
    SchematizedSize,
    SchematizedTile,
} from "../../../../graphql/types"
import { SchematizedTileType } from "../../../../graphql/types"
import type { SchematizedTileComponentItem } from "./types"
import { pipeline } from "../../../../utils/functional"
import { isPlaceOccupied } from "./selectors"
import { CategoryType } from "../../../../graphql/types"

export const getUpdatedSchematizedGroups = (
    newTile: SchematizedTileComponentItem,
    schematizedGroups: SchematizedPageGroup[],
    categoriesData: Maybe<CategoriesListResponse>,
    schematizedUiData: SchematizedTileComponentItem[]
): SchematizedPageGroup[] => {
    const isOccupied =
        newTile.x != null &&
        newTile.y != null &&
        isPlaceOccupied(schematizedUiData, newTile.x, newTile.y, newTile.gridId)

    if (isOccupied) {
        return schematizedGroups
    }

    const isCustomFolder = !newTile.type && !newTile.isBackButton

    if (newTile.type === CategoryType.Category || isCustomFolder) {
        // If folder (category or custom folder) is added
        let children = categoriesData ? categoriesData.data.filter(c => c.parentId === newTile.categoryId) : []

        if (children.length > 25) {
            children = children.splice(0, 24)
        }

        const newGridGroupId = uuid.v4()
        const newGridId = uuid.v4()
        const newGridGroup: SchematizedPageGroup = {
            id: newGridGroupId,
            pages: [
                {
                    id: newGridId,
                    tiles: [],
                },
            ],
        }
        const newFolderTile = composeNewFolderTile(newTile, newGridGroupId)
        const allChildrenLevelTiles = prepareFolderChildrenTiles(children, newGridId)
        // Add new tile
        let updatedSchematizedGroups = addTileToSchematizedGroups(newFolderTile, newTile.gridId, schematizedGroups)
        // Add new group
        updatedSchematizedGroups = [...updatedSchematizedGroups, newGridGroup]
        // Recursively call this function for all potential folder children tiles
        allChildrenLevelTiles.forEach(childrenTile => {
            updatedSchematizedGroups = getUpdatedSchematizedGroups(
                childrenTile,
                updatedSchematizedGroups,
                categoriesData,
                schematizedUiData
            )
        })
        return updatedSchematizedGroups
    } else {
        // If back button or product tile is added
        const tile = newTile.isBackButton ? composeNewBackBtnTile(newTile) : composeNewProductTile(newTile)
        return addTileToSchematizedGroups(tile, newTile.gridId, schematizedGroups)
    }
}

const addTileToSchematizedGroups = (
    newTile: SchematizedTile,
    gridId: Maybe<string>,
    schematizedGroups: SchematizedPageGroup[]
) => {
    return schematizedGroups.map(g => ({
        ...g,
        pages: g.pages.map(p => {
            if (gridId && p.id === gridId) {
                return { ...p, tiles: [...p.tiles, newTile] }
            }

            return p
        }),
    }))
}

export const projectStickyTiles = (schematizedData: SchematizedPageGroup[]): SchematizedPageGroup[] => {
    let result = schematizedData
    schematizedData.forEach(g => {
        g.pages.forEach(p => {
            // Go through each grid (page) and collect all sticky tiles
            const stickyTiles = p.tiles.filter(t => t.isSticky)

            if (stickyTiles.length < 1) {
                return
            }

            // Also look for all children grids this current grid is pointing to
            // so we can project the collected sticky tiles on those
            const childPageIds = getAllNestedPageChildIds(p, schematizedData)
            // Project sticky tiles to appropriate child grids
            result = result.map(resultGroup => ({
                ...resultGroup,
                pages: resultGroup.pages.map(resultPage => {
                    if (childPageIds.includes(resultPage.id)) {
                        return {
                            ...resultPage,
                            tiles: [
                                // Sticky tile has priority so remove all colliding grid tiles
                                ...resultPage.tiles.filter(
                                    t => !stickyTiles.some(stickyT => t.x === stickyT.x && t.y === stickyT.y)
                                ),
                                ...stickyTiles,
                            ],
                        }
                    }

                    return resultPage
                }),
            }))
        })
    })
    return result
}

const getAllNestedPageChildIds = (page: SchematizedPage, schematizedData: SchematizedPageGroup[]) => {
    const childPageIds = pipeline(
        page.tiles,
        map(t => {
            // @ts-expect-error
            const gridData = t.targetPageGroupId && schematizedData.find(group => group.id === t.targetPageGroupId)
            const p = gridData && head(gridData.pages)
            return p ? p.id : null
        }),
        reject(isNil)
    )
    const nestedChildPageIds = pipeline(
        schematizedData,
        // @ts-expect-error
        map(group => group.pages),
        flatten,
        // @ts-expect-error
        map(p => (childPageIds.includes(p.id) ? getAllNestedPageChildIds(p, schematizedData) : null)),
        flatten,
        reject(isNil)
    )
    // @ts-expect-error
    return [...childPageIds, ...nestedChildPageIds]
}

export const composeNewFolderTile = (
    newTile: SchematizedTileComponentItem,
    newGridGroupId: string
): SchematizedTile => {
    const isCustomFolder = !newTile.type && !newTile.isBackButton

    return {
        id: newTile.id || uuid.v4(),
        type: SchematizedTileType.Folder,
        x: newTile.x as number,
        y: newTile.y as number,
        targetPageGroupId: newGridGroupId,
        ...(isCustomFolder
            ? {
                  customFolder: {
                      name: newTile.name,
                      color: newTile.color,
                      icon: newTile.icon,
                  },
              }
            : {
                  categoryId: newTile.categoryId,
              }),
    }
}

export const composeNewBackBtnTile = (newTile: SchematizedTileComponentItem): SchematizedTile => {
    return {
        id: newTile.id || uuid.v4(),
        type: SchematizedTileType.BackButton,
        x: newTile.x as number,
        y: newTile.y as number,
    }
}

export const composeNewProductTile = (newTile: SchematizedTileComponentItem): SchematizedTile => {
    return {
        id: newTile.id || uuid.v4(),
        type: SchematizedTileType.ProductButton,
        x: newTile.x as number,
        y: newTile.y as number,
        categoryId: newTile.categoryId,
    }
}

export const prepareFolderChildrenTiles = (
    children: CategoriesListItem[],
    newGridId: string
): SchematizedTileComponentItem[] =>
    // @ts-expect-error
    flatten(
        range(0, 5).map(y =>
            range(0, 5)
                .map(x => {
                    const index = y * 5 + x
                    const childrenCat = children[index]
                    const tilePrototype = {
                        id: uuid.v4(),
                        gridId: newGridId,
                        x: x,
                        y: y,
                        rowspan: 1,
                        colspan: 1,
                        name: null,
                        color: null,
                        icon: null,
                        type: null,
                        price: null,
                        categoryId: null,
                        isBackButton: false,
                    }

                    // Back Button to center
                    if (y === 2 && x === 2) {
                        return { ...tilePrototype, isBackButton: true }
                    }

                    if (!childrenCat) {
                        return null
                    }

                    if (childrenCat.type === "product" || childrenCat.type === "category") {
                        return { ...tilePrototype, type: childrenCat.type, categoryId: childrenCat.id }
                    }

                    return null
                })
                .filter(a => a)
        )
    )

export const getLinkedGridId = (
    tile: SchematizedTile,
    currentGrid: SchematizedPage,
    schematizedData: SchematizedPageGroup[]
): Maybe<string> => {
    if (tile.type === SchematizedTileType.BackButton) {
        const currentGroup = schematizedData.find(g => g.pages.some(p => p.id === currentGrid.id))
        const backBtnPage =
            currentGroup &&
            pipeline(
                schematizedData,
                map(g =>
                    // @ts-expect-error
                    g.pages.map(p => {
                        if (p.tiles.some(t => t.targetPageGroupId === currentGroup.id)) {
                            return p
                        }

                        return null
                    })
                ),
                flatten,
                compact,
                head
            )
        // @ts-expect-error
        return backBtnPage ? backBtnPage.id : null
    } else if (tile.targetPageGroupId) {
        const linkedGridGroup = schematizedData.find(g => g.id === tile.targetPageGroupId)
        const linkedGrid = linkedGridGroup && head(linkedGridGroup.pages)
        return linkedGrid ? linkedGrid.id : null
    }

    return null
}

export const countCategoriesInSchematizedGroups = (categoryId: string, data: SchematizedPageGroup[]) =>
    pipeline(
        data,
        // @ts-expect-error
        map(g => g.pages.map(p => p.tiles.map(t => (t.categoryId === categoryId ? 1 : 0)))),
        flatten,
        sum
    )

export const getChangedLayouts = (
    newLayouts: SchematizedData[],
    oldLayouts: SchematizedData[],
    newSize: SchematizedSize
): SchematizedData[] =>
    newLayouts.filter(l => {
        const old = oldLayouts.find(ol => ol.layoutId === l.layoutId)
        return !old || (old && !equals(old, l)) || old.size !== newSize
    })

export const getFirstLayoutId = (data: SchematizedData[]): Maybe<string> => {
    const firstLayout = head(data)
    return firstLayout ? firstLayout.layoutId : null
}

// Remove all schematized groups that are not linked from any tile
// This is needed because in past when we were deleting tiles, we were not deleting the linked groups
// So we ended up with a lot of orphaned groups.
export const removeOrphanedSchematizedGroups = (data: SchematizedData[]) => {
    return data.map(layout => {
        const layoutTiles = pipeline(
            layout.data,
            map((d: SchematizedPageGroup) => d.pages),
            flatten,
            map((d: SchematizedPage) => d.tiles),
            flatten
        ) as SchematizedTile[]

        return {
            ...layout,
            data: layout.data.filter(
                (group: SchematizedPageGroup) =>
                    group.isRoot || layoutTiles.some((t: SchematizedTile) => t.targetPageGroupId === group.id)
            ),
        }
    })
}
